import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Bookmark, ChevronDown, Trash2, Plus, AlertCircle } from "lucide-react";

/**
 * "Presets ▾" dropdown next to the Aggregate button on the Competitions
 * page. Presets are named, saved selections of competition IDs so a
 * secretary doesn't have to re-tick the same rows every month.
 *
 * Behaviour:
 *  - Clicking the row applies that preset's selection to the parent's
 *    Set<compId> and (optionally) opens the aggregate modal — controlled
 *    via the onApply callback.
 *  - Save-current takes the parent's current Set as the seed and opens
 *    a tiny inline name prompt.
 *  - Delete lives on each row as a small trash icon.
 *
 * Props:
 *  - selectedIds: Set<string>  — current ticked competitions (for Save)
 *  - onApply({ competitionIds, openModal }) — called when a preset row
 *     is clicked; the parent replaces its selection Set with the
 *     preset's list, then optionally opens the modal.
 */
export function AggregatePresetsMenu({ selectedIds, onApply }) {
  const [open, setOpen] = useState(false);
  const [presets, setPresets] = useState(null); // null = not-yet-loaded
  const [saving, setSaving] = useState(false);
  const [savePromptOpen, setSavePromptOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const wrapRef = useRef(null);

  const canSaveCurrent = selectedIds.size >= 2;

  const load = async () => {
    try {
      const { data } = await api.get("/aggregate/presets");
      setPresets(data);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to load presets");
      setPresets([]);
    }
  };

  useEffect(() => {
    if (open && presets === null) load();
  }, [open, presets]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSavePromptOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const applyPreset = (p, openModal) => {
    if (!p.competition_ids?.length) {
      toast.error("This preset has no valid competitions left");
      return;
    }
    setOpen(false);
    onApply({ competitionIds: p.competition_ids, openModal });
  };

  const submitSave = async (e) => {
    e?.preventDefault?.();
    const name = saveName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await api.post("/aggregate/presets", {
        name,
        competition_ids: Array.from(selectedIds),
      });
      toast.success(`Preset "${name}" saved`);
      setSaveName("");
      setSavePromptOpen(false);
      await load();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to save preset");
    } finally {
      setSaving(false);
    }
  };

  const deletePreset = async (p, ev) => {
    ev.stopPropagation();
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    try {
      await api.delete(`/aggregate/presets/${p.id}`);
      setPresets((prev) => (prev || []).filter((x) => x.id !== p.id));
      toast.success("Preset deleted");
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to delete preset");
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="ccs-btn-ghost flex items-center gap-2 text-sm"
        data-testid="aggregate-presets-button"
        title="Named selections you can apply with one click"
      >
        <Bookmark size={14} /> Presets <ChevronDown size={12} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 z-40"
          style={{ background: "#0f0f0f", border: "1px solid #2a2a2a" }}
          data-testid="aggregate-presets-menu"
        >
          <div className="px-3 py-2 mono text-[10px] uppercase tracking-[0.25em] muted-text"
               style={{ borderBottom: "1px solid #1f1f1f" }}>
            Saved selections
          </div>

          <div className="max-h-72 overflow-y-auto">
            {presets === null && (
              <div className="px-3 py-4 text-sm muted-text">Loading…</div>
            )}
            {presets !== null && presets.length === 0 && (
              <div className="px-3 py-4 text-xs muted-text" data-testid="aggregate-presets-empty">
                No presets yet. Tick 2+ competitions below and use
                &ldquo;Save current selection&rdquo; to create one.
              </div>
            )}
            {(presets || []).map((p) => (
              <div
                key={p.id}
                className="group px-3 py-2 flex items-center gap-2 hover:bg-white/5 cursor-pointer"
                onClick={() => applyPreset(p, /* openModal */ true)}
                data-testid={`aggregate-preset-row-${p.id}`}
                title="Click to apply this preset and open the aggregate modal"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{p.name}</div>
                  <div className="mono text-[10px] muted-text">
                    {p.competition_ids.length} competition{p.competition_ids.length === 1 ? "" : "s"}
                    {p.stale_removed > 0 && (
                      <span className="text-amber-400 ml-2 inline-flex items-center gap-1"
                            title={`${p.stale_removed} competition${p.stale_removed === 1 ? "" : "s"} in this preset ${p.stale_removed === 1 ? "has" : "have"} been deleted since it was saved`}>
                        <AlertCircle size={10} /> {p.stale_removed} deleted
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    applyPreset(p, /* openModal */ false);
                  }}
                  className="text-xs muted-text hover:text-amber-400 md:opacity-0 md:group-hover:opacity-100 transition"
                  data-testid={`aggregate-preset-select-${p.id}`}
                  title="Just apply the selection — don't open the modal"
                >
                  select
                </button>
                <button
                  type="button"
                  onClick={(e) => deletePreset(p, e)}
                  className="muted-text hover:text-red-400 md:opacity-0 md:group-hover:opacity-100 transition"
                  data-testid={`aggregate-preset-delete-${p.id}`}
                  title="Delete preset"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>

          <div style={{ borderTop: "1px solid #1f1f1f" }}>
            {!savePromptOpen ? (
              <button
                type="button"
                onClick={() => {
                  if (!canSaveCurrent) {
                    toast.info("Tick at least two competitions first, then save the selection");
                    return;
                  }
                  setSavePromptOpen(true);
                }}
                disabled={!canSaveCurrent}
                className="w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="aggregate-preset-save-current"
              >
                <Plus size={14} className="text-amber-400" />
                <span className="flex-1">Save current selection…</span>
                <span className="mono text-[10px] muted-text">
                  {selectedIds.size} ticked
                </span>
              </button>
            ) : (
              <form onSubmit={submitSave} className="p-3 space-y-2">
                <label className="mono text-[10px] uppercase tracking-[0.25em] muted-text">
                  Name this preset
                </label>
                <input
                  type="text"
                  autoFocus
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder="e.g. PDI Rounds 2026"
                  maxLength={80}
                  className="ccs-input w-full text-sm"
                  data-testid="aggregate-preset-name-input"
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => { setSavePromptOpen(false); setSaveName(""); }}
                    className="text-xs muted-text hover:text-white px-2 py-1"
                    data-testid="aggregate-preset-save-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving || !saveName.trim()}
                    className="ccs-btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="aggregate-preset-save-submit"
                  >
                    {saving ? "Saving…" : "Save preset"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
